import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen, GraduationCap, Award, Target, Users, Globe2, FileText, Mountain, Waves, Layers } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';

const API = process.env.REACT_APP_BACKEND_URL;
const iconMap = { BookOpen, GraduationCap, Award, Target, Users, Globe2, FileText, Mountain, Waves, Layers };

const defaultServices = [
  { title: 'Abituriyent hazırlığı', icon: 'GraduationCap', desc: '8, 9, 10 və 11-ci siniflərin ali təhsil müəssisələrinə hazırlığı',
    full_desc: 'Abituriyent hazırlığı proqramımız şagirdləri ali təhsil müəssisələrinə qəbul imtahanlarına hərtərəfli hazırlayır. Fərdi yanaşma, müntəzəm test imtahanları və ətraflı izahlar ilə hər şagirdin potensialını maksimum dərəcədə reallaşdırmağa çalışırıq.',
    topics: ['Fiziki coğrafiya mövzuları', 'İqtisadi coğrafiya mövzuları', 'Azərbaycan coğrafiyası', 'Xəritə ilə iş bacarığı', 'Test strategiyaları və vaxt idarəetməsi'] },
  { title: 'MİQ hazırlığı', icon: 'BookOpen', desc: 'Müəllimlərin işə qəbulu üzrə müsabiqəyə hazırlıq',
    full_desc: 'Müəllimlərin İşə Qəbulu (MİQ) imtahanına hazırlıq proqramımız coğrafiya fənni üzrə ixtisas biliklərini möhkəmləndirməyə və imtahan formatına uyğun hazırlıq görməyə kömək edir.',
    topics: ['İxtisas fənni (Coğrafiya) sualları', 'Ümumi bilik sualları', 'Qanunvericilik sualları', 'Məntiq sualları', 'Test həll strategiyaları'] },
  { title: 'Sertifikasiya hazırlığı', icon: 'Award', desc: 'Müəllimlərin sertifikasiya imtahanına hazırlıq',
    full_desc: 'Sertifikasiya imtahanına hazırlıq proqramımız fəaliyyətdə olan coğrafiya müəllimlərinin peşəkarlıq səviyyəsini yüksəltməyə və sertifikat almağa kömək edir.',
    topics: ['Kurikulum və tədris metodları', 'Coğrafiya fənni üzrə dərin biliklər', 'Şagird qiymətləndirmə metodları', 'İnteraktiv dərs planlaşdırması', 'Pedaqoji texnologiyalar'] },
];

export default function ServicePage() {
  const { id } = useParams();
  const content = useSiteContent();
  const idx = parseInt(id) || 0;

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  const pg = content.services_page || {};
  const items = pg.our_services || [];
  const item = items[idx] || {};
  const def = defaultServices[idx] || defaultServices[0];

  const title = item.title || def.title;
  const desc = item.desc || def.desc;
  const fullDesc = item.full_desc || def.full_desc;
  const topics = item.topics || def.topics;
  const Icon = iconMap[item.icon || def.icon] || BookOpen;

  const others = (items.length > 0 ? items : defaultServices).filter((_, i) => i !== idx).slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative py-28 md:py-36 overflow-hidden bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#00B4D8]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-40 h-40 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-10 right-40 w-60 h-60 rounded-full border-2 border-white/20" />
          <div className="absolute top-40 right-20 w-20 h-20 rounded-full border-2 border-white/20" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/services" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Xidmətlərə qayıt
            </Link>
            <div className="flex items-center gap-5 mb-6">
              <div className="w-20 h-20 rounded-3xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-xl">
                <Icon className="w-10 h-10 text-white" />
              </div>
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-white text-sm font-bold mb-2 border border-white/20">Xidmət</span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">{title}</h1>
              </div>
            </div>
            <p className="text-lg text-white font-semibold max-w-2xl">{desc}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <p className="text-lg text-[#5C6B7A] leading-relaxed mb-10">{fullDesc}</p>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1A2530] mb-6">Proqram Mövzuları</h2>
              <div className="space-y-3">
                {(Array.isArray(topics) ? topics : []).map((topic, i) => (
                  <motion.div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[#F4F7F6] border border-[#E2E8F0]"
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{i + 1}</span>
                    </div>
                    <span className="text-[#1A2530] font-medium">{topic}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0077B6] to-[#00B4D8] rounded-3xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Qeydiyyatdan keçin</h3>
              <p className="text-white/80 mb-6">Bu xidmət haqqında ətraflı məlumat almaq üçün bizimlə əlaqə saxlayın</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 rounded-2xl bg-white text-[#0077B6] font-bold hover:shadow-lg transition-all group">
                Bizimlə Əlaqə <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other Services */}
      {others.length > 0 && (
        <section className="py-16 bg-[#F4F7F6]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl font-bold text-[#1A2530] mb-8">Digər Xidmətlər</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((other, i) => {
                const OIcon = iconMap[other.icon || defaultServices[0].icon] || BookOpen;
                const origIdx = (items.length > 0 ? items : defaultServices).indexOf(other);
                return (
                  <Link key={i} to={`/service/${origIdx >= 0 ? origIdx : i}`} className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-[#E2E8F0]">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <OIcon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1A2530] mb-2 group-hover:text-[#0077B6] transition-colors">{other.title || defaultServices[i]?.title}</h3>
                    <p className="text-sm text-[#5C6B7A] mb-3">{other.desc || defaultServices[i]?.desc}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0077B6]">Ətraflı <ArrowRight className="w-3.5 h-3.5" /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
